import { useCallback, useMemo, Fragment } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HR from 'UIComponents/elements/HR';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UISelect from 'UIComponents/input/UISelect';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { getIndustryOptions, getEmployeesOptions } from '../utils/options';
import { FILTER_KEY, FILTER, INDUSTRY_KEY, EMPLOYEES_KEY, SERVICE_KEY, SORT_KEY, SORT } from '../constants/ReviewsListQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ValueComponent = ({
  children
}) => /*#__PURE__*/_jsx("span", {
  role: "button",
  children: children
});
export default function ReviewsFilterSortDropdowns({
  catalogServices,
  excludedFilterOptions,
  onUpdateFilters,
  reviewFilters,
  reviewStats,
  showDivider = true
}) {
  const {
    display,
    textAlign
  } = useResponsiveProps({
    display: {
      xs: 'block',
      md: 'inherit'
    },
    textAlign: {
      xs: 'left',
      xl: catalogServices ? 'left' : 'right'
    }
  });
  const {
    buttonUse,
    className
  } = useResponsiveProps({
    buttonUse: {
      xs: 'link',
      lg: 'transparent'
    },
    className: {
      xs: 'm-left-4',
      lg: 'm-left-0'
    }
  });
  const validFilters = useMemo(() => reviewStats.getValidRatingFilters(), [reviewStats]);
  const buildDropdown = useCallback((textKey, test) => {
    const options = Object.keys(test).reduce((result, option) => {
      if (!excludedFilterOptions || !excludedFilterOptions.includes(option)) {
        result.push({
          text: I18n.text(`marketplace-ui-reviews.list.${textKey}.${option}`),
          value: option,
          disabled: textKey === FILTER_KEY && option !== FILTER.ALL && !validFilters.has(option)
        });
      }
      return result;
    }, []);
    return /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          fontWeight: 600,
          display
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.list.${textKey}.label`
        })
      }), /*#__PURE__*/_jsx(UISelect, {
        "data-test-id": `${textKey}-reviews-list-select`,
        className: className,
        buttonUse: buttonUse,
        menuWidth: 250,
        options: options,
        onChange: ({
          target: {
            value
          }
        }) => onUpdateFilters({
          textKey,
          value,
          source: 'dropdown filter'
        }),
        value: reviewFilters.get(textKey),
        valueComponent: ValueComponent
      }, textKey)]
    });
  }, [buttonUse, className, display, excludedFilterOptions, onUpdateFilters, reviewFilters, validFilters]);
  const renderedFilterDropdown = buildDropdown(FILTER_KEY, FILTER);
  const renderedSortDropdown = buildDropdown(SORT_KEY, SORT);
  const industryOptions = useMemo(getIndustryOptions, []);
  const renderedIndustryDropdown = /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": "industry-reviews-list-select",
    className: className,
    buttonUse: buttonUse,
    clearable: true,
    menuWidth: 250,
    options: industryOptions,
    onChange: ({
      target: {
        value
      }
    }) => onUpdateFilters({
      textKey: INDUSTRY_KEY,
      value,
      source: 'dropdown filter'
    }),
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-reviews.industry.placeholder"
    }),
    value: reviewFilters.get(INDUSTRY_KEY),
    valueComponent: ValueComponent
  }, INDUSTRY_KEY);
  const employeesOptions = useMemo(getEmployeesOptions, []);
  const renderedEmployeesDropdown = /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": "employees-reviews-list-select",
    className: className,
    buttonUse: buttonUse,
    clearable: true,
    menuWidth: 250,
    options: employeesOptions,
    onChange: ({
      target: {
        value
      }
    }) => onUpdateFilters({
      textKey: EMPLOYEES_KEY,
      value,
      source: 'dropdown filter'
    }),
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-reviews.employees.placeholder"
    }),
    value: reviewFilters.get(EMPLOYEES_KEY),
    valueComponent: ValueComponent
  }, EMPLOYEES_KEY);
  const renderedCatalogServicesDropdown = catalogServices && /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": "services-reviews-list-select",
    className: className,
    buttonUse: buttonUse,
    clearable: true,
    menuWidth: 250,
    options: catalogServices.map(({
      id,
      name
    }) => ({
      value: id,
      text: name
    })),
    onChange: ({
      target: {
        value
      }
    }) => onUpdateFilters({
      textKey: SERVICE_KEY,
      value,
      source: 'dropdown filter'
    }),
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-reviews.services.placeholder"
    }),
    value: reviewFilters.get(SERVICE_KEY.toLowerCase()),
    valueComponent: ValueComponent
  }, SERVICE_KEY);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(UIGrid, {
      children: [/*#__PURE__*/_jsxs(UIGridItem, {
        size: {
          xs: 12,
          xl: catalogServices ? 12 : 9
        },
        children: [renderedFilterDropdown, renderedIndustryDropdown, renderedEmployeesDropdown, renderedCatalogServicesDropdown]
      }), /*#__PURE__*/_jsx(UIGridItem, {
        size: {
          xs: 12,
          xl: catalogServices ? 12 : 3
        },
        style: {
          textAlign
        },
        children: renderedSortDropdown
      })]
    }), showDivider && /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    })]
  });
}